/**
 * Platform Detection Hook v1.0
 * 
 * Comprehensive platform detection for:
 * - Native APK (Capacitor)
 * - PWA (installed web app)
 * - Browser (regular web)
 * - Mobile vs Desktop
 * 
 * Used to:
 * - Show/hide install prompts
 * - Enable/disable native features
 * - Optimize performance per platform
 */

import { useState, useEffect } from 'react';

/**
 * Check if running on Capacitor native platform
 */
function checkCapacitorNative(): boolean {
    try {
        if (typeof (window as any).Capacitor !== 'undefined') {
            return (window as any).Capacitor.isNativePlatform();
        }
    } catch (e) {}
    return false;
}

/**
 * Get Capacitor platform name
 */
function getCapacitorPlatform(): string {
    try {
        if (typeof (window as any).Capacitor !== 'undefined') {
            return (window as any).Capacitor.getPlatform();
        }
    } catch (e) {}
    return 'web';
}

export type PlatformType = 'apk' | 'pwa' | 'browser';
export type DeviceType = 'mobile' | 'tablet' | 'desktop';
export type OSType = 'android' | 'ios' | 'windows' | 'macos' | 'linux' | 'unknown';

export interface PlatformInfo {
    // Platform type
    platform: PlatformType;
    isAPK: boolean;
    isPWA: boolean;
    isBrowser: boolean;
    
    // Device type
    device: DeviceType;
    isMobile: boolean;
    isTablet: boolean;
    isDesktop: boolean;
    
    // OS
    os: OSType;
    isAndroid: boolean;
    isIOS: boolean;
    
    // Installation status
    isInstalled: boolean;
    canInstall: boolean;
    shouldShowInstallPrompt: boolean;
    
    // Capabilities
    supportsNotifications: boolean;
    supportsHaptics: boolean;
    supportsOffline: boolean;
    supportsShare: boolean;
    
    // Performance hints
    isLowEndDevice: boolean;
    recommendedFPS: 30 | 60 | 120;
    shouldReduceAnimations: boolean;
    
    // Display
    isStandalone: boolean;
    isFullscreen: boolean;
    hasNotch: boolean;
    safeAreaInsets: {
        top: number;
        bottom: number;
        left: number;
        right: number;
    };
}

export function usePlatformDetection(): PlatformInfo {
    const [info, setInfo] = useState<PlatformInfo>(() => detectPlatform());

    useEffect(() => {
        // Re-detect on display mode change
        const mediaQuery = window.matchMedia('(display-mode: standalone)');
        const handleChange = () => {
            setInfo(detectPlatform());
        };
        
        mediaQuery.addEventListener('change', handleChange);
        
        // Re-detect on visibility change (app might have been installed)
        document.addEventListener('visibilitychange', () => {
            if (!document.hidden) {
                setInfo(detectPlatform());
            }
        });
        
        return () => {
            mediaQuery.removeEventListener('change', handleChange);
        };
    }, []);

    return info;
}

function detectPlatform(): PlatformInfo {
    const ua = navigator.userAgent.toLowerCase();
    
    // ==================== Platform Detection ====================
    
    // Check if running in Capacitor (native APK/IPA)
    const isNative = checkCapacitorNative();
    const capacitorPlatform = getCapacitorPlatform();
    
    // Check if installed as PWA
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    const isIOSStandalone = (window.navigator as any).standalone === true;
    const isFullscreen = window.matchMedia('(display-mode: fullscreen)').matches;
    const isPWA = (isStandalone || isIOSStandalone || isFullscreen) && !isNative;
    
    // Determine platform type
    let platform: PlatformType = 'browser';
    if (isNative) {
        platform = 'apk';
    } else if (isPWA) {
        platform = 'pwa';
    }
    
    // ==================== Device Detection ====================
    
    const isMobileUA = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(ua);
    const isTabletUA = /ipad|tablet|playbook|silk/i.test(ua) || 
                       (ua.includes('android') && !ua.includes('mobile'));
    
    // Use screen size as fallback
    const screenWidth = window.screen.width;
    const screenHeight = window.screen.height;
    const minDimension = Math.min(screenWidth, screenHeight);
    const maxDimension = Math.max(screenWidth, screenHeight);
    
    let device: DeviceType = 'desktop';
    if (isTabletUA || (minDimension >= 600 && maxDimension < 1200)) {
        device = 'tablet';
    } else if (isMobileUA || minDimension < 600) {
        device = 'mobile';
    }
    
    // ==================== OS Detection ====================
    
    let os: OSType = 'unknown';
    if (/android/i.test(ua)) {
        os = 'android';
    } else if (/ipad|iphone|ipod/i.test(ua)) {
        os = 'ios';
    } else if (/windows/i.test(ua)) {
        os = 'windows';
    } else if (/macintosh|mac os/i.test(ua)) {
        os = 'macos';
    } else if (/linux/i.test(ua)) {
        os = 'linux';
    }
    
    // ==================== Installation Status ====================
    
    const isInstalled = isNative || isPWA;
    
    // Can install if not already installed and on a supported platform
    const canInstall = !isInstalled && (os === 'android' || os === 'ios' || 'BeforeInstallPromptEvent' in window);
    
    // Should show install prompt if:
    // - Not installed
    // - Can install
    // - Haven't dismissed recently
    const lastDismissed = localStorage.getItem('install-prompt-dismissed');
    const dismissedRecently = lastDismissed && 
        (Date.now() - parseInt(lastDismissed)) < 7 * 24 * 60 * 60 * 1000; // 7 days
    
    const shouldShowInstallPrompt = canInstall && !dismissedRecently;
    
    // ==================== Capabilities ====================
    
    const supportsNotifications = isNative || ('Notification' in window);
    const supportsHaptics = isNative || ('vibrate' in navigator);
    const supportsOffline = 'serviceWorker' in navigator;
    const supportsShare = isNative || ('share' in navigator);
    
    // ==================== Performance Hints ====================
    
    // Detect low-end device
    const deviceMemory = (navigator as any).deviceMemory || 4;
    const hardwareConcurrency = navigator.hardwareConcurrency || 4;
    const isLowEndDevice = deviceMemory < 4 || hardwareConcurrency < 4;
    
    // Recommend FPS based on device capability
    let recommendedFPS: 30 | 60 | 120 = 60;
    if (isLowEndDevice) {
        recommendedFPS = 30;
    } else if (deviceMemory >= 8 && hardwareConcurrency >= 8) {
        recommendedFPS = 120;
    }
    
    // Should reduce animations on low-end or if user prefers
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const shouldReduceAnimations = isLowEndDevice || prefersReducedMotion;
    
    // ==================== Display ====================
    
    // Detect notch (iOS)
    const hasNotch = CSS.supports('padding-top: env(safe-area-inset-top)') &&
                     parseInt(getComputedStyle(document.documentElement).getPropertyValue('--sat') || '0') > 0;
    
    // Get safe area insets
    const computedStyle = getComputedStyle(document.documentElement);
    const safeAreaInsets = {
        top: parseInt(computedStyle.getPropertyValue('--safe-area-inset-top') || '0') || 0,
        bottom: parseInt(computedStyle.getPropertyValue('--safe-area-inset-bottom') || '0') || 0,
        left: parseInt(computedStyle.getPropertyValue('--safe-area-inset-left') || '0') || 0,
        right: parseInt(computedStyle.getPropertyValue('--safe-area-inset-right') || '0') || 0,
    };
    
    return {
        // Platform
        platform,
        isAPK: isNative,
        isPWA,
        isBrowser: platform === 'browser',
        
        // Device
        device,
        isMobile: device === 'mobile',
        isTablet: device === 'tablet',
        isDesktop: device === 'desktop',
        
        // OS
        os,
        isAndroid: os === 'android',
        isIOS: os === 'ios',
        
        // Installation
        isInstalled,
        canInstall,
        shouldShowInstallPrompt,
        
        // Capabilities
        supportsNotifications,
        supportsHaptics,
        supportsOffline,
        supportsShare,
        
        // Performance
        isLowEndDevice,
        recommendedFPS,
        shouldReduceAnimations,
        
        // Display
        isStandalone: isStandalone || isIOSStandalone,
        isFullscreen,
        hasNotch,
        safeAreaInsets,
    };
}

/**
 * Quick utility to check if we should show install prompt
 */
export function shouldShowInstall(): boolean {
    const info = detectPlatform();
    return info.shouldShowInstallPrompt;
}

/**
 * Quick utility to check if running in native app
 */
export function isNativeApp(): boolean {
    return checkCapacitorNative();
}

/**
 * Quick utility to check if running as PWA
 */
export function isPWAMode(): boolean {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    const isIOSStandalone = (window.navigator as any).standalone === true;
    return (isStandalone || isIOSStandalone) && !checkCapacitorNative();
}

/**
 * Mark install prompt as dismissed
 */
export function dismissInstallPrompt(): void {
    localStorage.setItem('install-prompt-dismissed', Date.now().toString());
}

export default usePlatformDetection;
